import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { datasetAPI } from '../services/api';

function DatasetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dataset, setDataset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchDataset();
  }, [id]);

  const fetchDataset = async () => {
    try {
      setLoading(true);
      // Load preview instead of full dataset for performance
      const response = await datasetAPI.getPreview(id);
      setDataset(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this dataset?')) {
      return;
    }

    try {
      await datasetAPI.delete(id);
      navigate('/datasets');
    } catch (err) {
      alert('Failed to delete dataset: ' + err.message);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await datasetAPI.download(id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(dataset.name || 'dataset').replace(/[^a-zA-Z0-9._-]+/g, '_')}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download dataset: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  const formatFileSize =(bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(2)} KB`;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !dataset) {
    return (
      <div className="card">
        <div className="alert alert-danger">
          {error || 'Dataset not found'}
        </div>
        <Link to="/datasets" className="btn btn-secondary">Back to Datasets</Link>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="flex-between mb-3">
          <h2>{dataset.name}</h2>
          <div className="flex">
            <button onClick={handleDownload} className="btn btn-primary" disabled={downloading}>
              {downloading ? 'Downloading...' : 'Download CSV'}
            </button>
            <button onClick={handleDelete} className="btn btn-danger">
              Delete
            </button>
            <Link to="/datasets" className="btn btn-secondary">
              Back
            </Link>
          </div>
        </div>

        <div className="grid grid-2">
          <div>
            <h3>Dataset Information</h3>
            <p><strong>Name:</strong> {dataset.name}</p>
            {dataset.description && (
              <p><strong>Description:</strong> {dataset.description}</p>
            )}
            <p><strong>Total Rows:</strong> {dataset.rowCount.toLocaleString()}</p>
            <p><strong>Columns:</strong> {dataset.columns?.length || 0}</p>
            <p><strong>File Size:</strong> {formatFileSize(dataset.fileSize)}</p>
            {dataset.tags && dataset.tags.length > 0 && (
              <p>
                <strong>Tags:</strong>{' '}
                {dataset.tags.map(tag => (
                  <span key={tag} className="badge badge-info" style={{ marginRight: '0.25rem' }}>
                    {tag}
                  </span>
                ))}
              </p>
            )}
          </div>

          <div>
            <h3>Columns</h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', padding: '0.5rem', borderRadius: '4px' }}>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                {dataset.columns?.map((col, idx) => (
                  <li key={idx}>{col}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-3">
          <h3>Data Preview</h3>
          <p style={{ color: '#666', fontSize: '0.875rem' }}>
            Showing first 100 rows of {dataset.rowCount.toLocaleString()} total rows
          </p>

          <div style={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#f5f5f5', zIndex: 1 }}>
                <tr style={{ borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderRight: '1px solid #ddd' }}>#</th>
                  {dataset.columns?.map(col => (
                    <th key={col} style={{ padding: '0.5rem', textAlign: 'left', borderRight: '1px solid #ddd' }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataset.data?.map((row, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.5rem', borderRight: '1px solid #eee', background: '#fafafa' }}>
                      {idx + 1}
                    </td>
                    {dataset.columns?.map(col => (
                      <td key={col} style={{ padding: '0.5rem', borderRight: '1px solid #eee' }}>
                        {row[col] !== undefined && row[col] !== null ? String(row[col]) : <em style={{ color: '#999' }}>null</em>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-3">
          <p><strong>Created:</strong> {new Date(dataset.createdAt).toLocaleString()}</p>
          <p><strong>Last Updated:</strong> {new Date(dataset.updatedAt).toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}

export default DatasetDetail;
